import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthLogEvent } from '@/types/auth.types';
import { Capacitor } from '@capacitor/core';

type OAuthProvider = 'apple' | 'google';

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  logs: AuthLogEvent[];
  mfaEnroll: () => Promise<{ qr_code?: string; secret?: string; factor_id?: string; error?: any }>;
  mfaVerify: (factorId: string, code: string) => Promise<{ error?: any }>;
  mfaChallenge: () => Promise<{ challenge_id?: string; error?: any }>;
  mfaVerifyChallenge: (challengeId: string, code: string) => Promise<{ error?: any; session?: any }>;
  getMfaStatus: () => Promise<{ enabled: boolean; factors: any[] }>;
  signInWithProvider: (
    provider: OAuthProvider,
    opts?: { redirectTo?: string }
  ) => Promise<Awaited<ReturnType<typeof supabase.auth.signInWithOAuth>>>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState<AuthLogEvent[]>([]);

  const addLog = (level: AuthLogEvent['level'], message: string, metadata?: Record<string, any>) => {
    const logEvent: AuthLogEvent = {
      level,
      message,
      timestamp: new Date().toISOString(),
      userId: user?.id,
      metadata
    };
    setLogs(prev => [...prev, logEvent]);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      addLog('info', `Auth state changed: ${event}`, { event, userId: session?.user?.id });
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      if (session) addLog('info', 'Existing session found', { userId: session.user.id });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    const redirectUrl = 'https://prayai.aitwus.org/auth/callback';
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl, data: metadata }
    });
    if (error) addLog('error', `Sign up failed: ${error.message}`, { email, error: error.message });
    else addLog('info', 'Sign up successful', { email });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) addLog('error', `Sign in failed: ${error.message}`, { email, error: error.message });
    else addLog('info', 'Sign in successful', { email });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) addLog('error', `Sign out failed: ${error.message}`, { error: error.message });
    else addLog('info', 'Sign out successful');
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://prayai.aitwus.org/auth/callback'
    });
    if (error) addLog('error', `Password reset failed: ${error.message}`, { email, error: error.message });
    else addLog('info', 'Password reset email sent', { email });
    return { error };
  };

  // Platform-aware OAuth with correct redirectTo and skipBrowserRedirect on native
  const signInWithProvider: UseAuthReturn['signInWithProvider'] = async (provider, opts) => {
    const isNative = Capacitor.isNativePlatform();
    const redirectTo = isNative
      ? 'prayai://auth/callback'
      : 'https://prayai.aitwus.org/auth/callback';

    addLog('info', 'OAuth sign-in start', { provider, redirectTo, isNative });

    const result = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: isNative
      }
    });

    if (result.error) {
      addLog('error', `OAuth sign-in failed: ${result.error.message}`, { provider });
    } else {
      addLog('info', 'OAuth sign-in initiated', {
        provider,
        hasUrl: !!result.data?.url
      });
    }

    return result;
  };

  // MFA methods
  const mfaEnroll = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('mfa-enrollment', {
        body: { action: 'enroll' }
      });
      
      if (error) {
        addLog('error', `MFA enrollment failed: ${error.message}`);
        return { error };
      }
      
      addLog('info', 'MFA enrollment started');
      return data;
    } catch (error: any) {
      addLog('error', `MFA enrollment error: ${error.message}`);
      return { error };
    }
  };

  const mfaVerify = async (factorId: string, code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('mfa-enrollment', {
        body: { action: 'verify', factor_id: factorId, code }
      });
      
      if (error) {
        addLog('error', `MFA verification failed: ${error.message}`);
        return { error };
      }
      
      addLog('info', 'MFA verification successful');
      return { success: true };
    } catch (error: any) {
      addLog('error', `MFA verification error: ${error.message}`);
      return { error };
    }
  };

  const mfaChallenge = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('mfa-enrollment', {
        body: { action: 'challenge' }
      });
      
      if (error) {
        addLog('error', `MFA challenge failed: ${error.message}`);
        return { error };
      }
      
      addLog('info', 'MFA challenge created');
      return data;
    } catch (error: any) {
      addLog('error', `MFA challenge error: ${error.message}`);
      return { error };
    }
  };

  const mfaVerifyChallenge = async (challengeId: string, code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('mfa-enrollment', {
        body: { action: 'verify_challenge', challenge_id: challengeId, code }
      });
      
      if (error) {
        addLog('error', `MFA challenge verification failed: ${error.message}`);
        return { error };
      }
      
      addLog('info', 'MFA challenge verification successful');
      return data;
    } catch (error: any) {
      addLog('error', `MFA challenge verification error: ${error.message}`);
      return { error };
    }
  };

  const getMfaStatus = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const enabled = factors?.totp && factors.totp.length > 0;
      addLog('info', 'MFA status retrieved', { enabled });
      return { enabled, factors: factors?.totp || [] };
    } catch (error: any) {
      addLog('error', `MFA status error: ${error.message}`);
      return { enabled: false, factors: [] };
    }
  };

  return {
    user,
    session,
    isLoading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    logs,
    mfaEnroll,
    mfaVerify,
    mfaChallenge,
    mfaVerifyChallenge,
    getMfaStatus,
    signInWithProvider
  };
}
